import type {
  BaseHealthProfileMedicationToVerify,
  ExternalHealthProfileMedicationToVerify,
  HealthProfileMedicationToVerifyResult,
  HealthProfileMedicationToVerifyWithSearch,
  HealthProfileMedicationTransferrable,
  InternalHealthProfileMedicationToVerify,
  SearchHealthProfileMedicationToVerify,
} from 'src/app/hooks/data/healthProfile';
import type { MedicationSearchResult } from 'src/app/types';

export interface MedicationEligibilityResult {
  eligibleMeds: HealthProfileMedicationTransferrable[];
  ineligibleMeds: HealthProfileMedicationTransferrable[];
  doctorRequiredMeds: HealthProfileMedicationTransferrable[];
  capsuleMeds: InternalHealthProfileMedicationToVerify[];
}

export const getMedicationsTransferEligibility = (
  medications: HealthProfileMedicationToVerifyWithSearch[]
): MedicationEligibilityResult =>
  medications.reduce(
    (result, med) => {
      if (!!med.isCapsuleMed) {
        result.capsuleMeds.push(med);
      } else if (!!med.eligibility.eligibleForTransfer && !med.eligibility.doctorRequiredForTransfer) {
        result.eligibleMeds.push(med);
      } else if (!!med.eligibility.eligibleForTransfer && !!med.eligibility.doctorRequiredForTransfer) {
        result.doctorRequiredMeds.push(med);
      } else {
        result.ineligibleMeds.push(med);
      }
      return result;
    },
    { eligibleMeds: [], ineligibleMeds: [], capsuleMeds: [], doctorRequiredMeds: [] } as MedicationEligibilityResult
  );

export const formatPrescriberNotes = (medication: HealthProfileMedicationToVerifyWithSearch) => {
  /*
    No prescribers are gonna be available for Capsule meds or meds from search.
    Also don't add a note if we don't have minimum data to help PX with transfers, exit early.
  */
  if (
    !('prescriber' in medication) ||
    !!medication.isCapsuleMed ||
    (!medication.prescriber.firstName && !medication.prescriber.phone && !medication.prescriber.fax)
  ) {
    return '';
  }

  const prescriberName = `${medication.prescriber.firstName} ${medication.prescriber.lastName}`;
  const prescriberStreet = `${medication.prescriber.street}${
    !!medication.prescriber.street2 ? ' ' + medication.prescriber.street2 : ''
  }`;
  const prescriberLocation = `${medication.prescriber.city}, ${medication.prescriber.state} ${medication.prescriber.zipCode}`;
  const prescriberPhone = medication.prescriber.phone;
  const prescriberFax = medication.prescriber.fax;

  return `Prescriber: ${prescriberName}\nPrescriber address: ${prescriberStreet}, ${prescriberLocation}\nPrescriber telephone number: ${prescriberPhone}\nPrescriber fax number: ${prescriberFax}`;
};

/*
 * The helpers below replace the "." with unicode "․" to avoid Formik using it as a property accessor, which
 * breaks how it writes to the local feature data storage.
 */
export const formatStrengthAndLabel = (medication: BaseHealthProfileMedicationToVerify) =>
  replaceDotWithUnicode(
    `${!!medication.strength ? medication.strength : ''}${
      !!medication.dosageFormLabel ? ' ' + medication.dosageFormLabel : ''
    }`
  );

export const formatMedicationWithStrengthAndLabel = (medication: BaseHealthProfileMedicationToVerify) =>
  replaceDotWithUnicode(`${medication.primaryName} ${formatStrengthAndLabel(medication)}`);

/*
  We don't check for lastFillDate existing before using it here because we filter out medications with
  no last fill date in previous steps. If that changes, we should start checking for it.
*/
export const formatMedicationDescriptionWithStrengthLabelAndCapsule = (
  medication: HealthProfileMedicationToVerifyResult
) =>
  replaceDotWithUnicode(`${formatStrengthAndLabel(medication)}${
    medication.isCapsuleMed && medication.lastFillDate
      ? '\nFilled by Capsule on ' + new Date(medication.lastFillDate).toLocaleDateString('en-US')
      : ''
  }
  `);

/*
 * Replace the "." with unicode "․" to avoid Formik using it as a property accessor. This has broken Formik
 * functionality before.
 */
export const replaceDotWithUnicode = (str: string) => str.replace('.', '․');

export const convertSearchToHealthProfileMedication = (
  med: MedicationSearchResult
): SearchHealthProfileMedicationToVerify => ({
  ...med,
  primaryName: med.primaryName,
  eligibility: {
    doctorRequiredForTransfer: med.doctorRequiredForTransfer ?? false,
    eligibleForTransfer: med.eligibleForTransfer ?? false,
  },
  isCapsuleMed: false,
  pharmacy: {
    name: 'contact customer',
    phone: '',
    street: '',
  },
  hiclSeqno: med.hiclSeqno ?? '',
});

// Split between Capsule, non-Capsule, and search medications
export const splitInternalExternalSearchedMedications = (medications: HealthProfileMedicationToVerifyWithSearch[]) =>
  medications.reduce(
    (acc, medication) => {
      if ('externalId' in medication) {
        acc.externalMedications.push(medication);
      } else if ('id' in medication) {
        acc.capsuleMedications.push(medication);
      } else {
        acc.searchedMedications.push(medication);
      }

      return acc;
    },
    {
      externalMedications: [] as ExternalHealthProfileMedicationToVerify[],
      capsuleMedications: [] as InternalHealthProfileMedicationToVerify[],
      searchedMedications: [] as SearchHealthProfileMedicationToVerify[],
    }
  );
